/**
 * Daily Mixing Cost — per-day rollup of cotton consumed in laydowns.
 *
 * For each calendar day in the chosen window, shows: laydowns count,
 * bales, total kg, weighted-avg ₹/kg, total ₹. Click a row to drill
 * into per-laydown breakdown.
 *
 * Source: GET /bale-mgmt/cost/daily-mixing
 *         GET /bale-mgmt/cost/daily-mixing/drilldown?on_date=
 */
import { useMemo, useState } from 'react'
import {
  Card, Col, DatePicker, Drawer, Empty, Row, Space, Statistic, Table, Tag, Typography,
} from 'antd'
import { LineChartOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import apiClient from '@/shared/api/client'
import { usePlantId } from '@/shared/hooks/usePlantId'
import PageHeader from '@/shared/components/PageHeader'

const { Text } = Typography
const { RangePicker } = DatePicker

interface DailyRow {
  day: string
  laydown_count: number
  bales: number
  // Sprint 0070 — Gross / Tare / Net split
  gross_kg: number
  tare_kg: number
  net_kg: number
  total_kg: number  // legacy alias = net_kg
  avg_cost_per_kg: number | null
  total_value: number
}

interface DrillRow {
  id: string
  laydown_no: string
  count_ne: number | null
  count_description: string | null
  bales: number
  weight_kg: number
  cost_per_kg: number | null
  total_value: number
  status: string
  blowroom_line_name: string | null
}

const fmt = (n?: number | null, dp = 2) =>
  n != null ? n.toLocaleString('en-IN', { minimumFractionDigits: dp, maximumFractionDigits: dp }) : '—'
const inr = (n?: number | null) => (n != null ? `₹${fmt(n, 0)}` : '—')

export default function DailyMixingCostPage() {
  const { plantId } = usePlantId()
  const [range, setRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>(() => [
    dayjs().subtract(30, 'day'),
    dayjs(),
  ])
  const [drillDate, setDrillDate] = useState<string | null>(null)

  const { data: rows = [], isLoading } = useQuery<DailyRow[]>({
    queryKey: ['daily-mixing-cost', plantId, range[0].format('YYYY-MM-DD'), range[1].format('YYYY-MM-DD')],
    queryFn: () =>
      apiClient
        .get('/bale-mgmt/cost/daily-mixing', {
          params: {
            plant_id: plantId,
            from_date: range[0].format('YYYY-MM-DD'),
            to_date: range[1].format('YYYY-MM-DD'),
          },
        })
        .then(r => r.data),
    enabled: !!plantId,
  })

  const { data: drillRows = [] } = useQuery<DrillRow[]>({
    queryKey: ['daily-mixing-drill', plantId, drillDate],
    queryFn: () =>
      apiClient
        .get('/bale-mgmt/cost/daily-mixing/drilldown', {
          params: { plant_id: plantId, on_date: drillDate },
        })
        .then(r => r.data),
    enabled: !!plantId && !!drillDate,
  })

  // KPI summary across the visible window
  const summary = useMemo(() => {
    const totalKg = rows.reduce((s, r) => s + (r.total_kg || 0), 0)
    const totalValue = rows.reduce((s, r) => s + (r.total_value || 0), 0)
    const bales = rows.reduce((s, r) => s + (r.bales || 0), 0)
    const laydowns = rows.reduce((s, r) => s + (r.laydown_count || 0), 0)
    const avg = totalKg > 0 ? totalValue / totalKg : null
    return { totalKg, totalValue, bales, laydowns, avg }
  }, [rows])

  return (
    <div>
      <PageHeader
        title="Daily Mixing Cost"
        subtitle="Per-day cotton-consumption value across approved laydowns. Click any day to drill into the laydowns that ran."
        extra={
          <RangePicker
            value={range}
            onChange={v => v && v[0] && v[1] && setRange([v[0], v[1]])}
            format="DD-MMM-YYYY"
            allowClear={false}
          />
        }
      />

      {/* KPI tiles for the window */}
      <Row gutter={12} style={{ marginBottom: 16 }}>
        <Col xs={6}>
          <Card size="small" style={{ borderTop: '3px solid #1677ff' }}>
            <Statistic title="Laydowns" value={summary.laydowns}
              valueStyle={{ fontSize: 22, color: '#1677ff', fontWeight: 700 }} />
          </Card>
        </Col>
        <Col xs={6}>
          <Card size="small" style={{ borderTop: '3px solid #722ed1' }}>
            <Statistic title="Bales" value={summary.bales}
              valueStyle={{ fontSize: 22, color: '#722ed1', fontWeight: 700 }} />
          </Card>
        </Col>
        <Col xs={6}>
          <Card size="small" style={{ borderTop: '3px solid #52c41a' }}>
            <Statistic
              title="Avg ₹/kg"
              value={summary.avg ? fmt(summary.avg, 2) : '—'}
              valueStyle={{ fontSize: 22, color: '#52c41a', fontWeight: 700 }}
              prefix="₹"
            />
          </Card>
        </Col>
        <Col xs={6}>
          <Card size="small" style={{ borderTop: '3px solid #faad14' }}>
            <Statistic
              title="Total Value"
              value={fmt(summary.totalValue, 0)}
              valueStyle={{ fontSize: 22, color: '#faad14', fontWeight: 700 }}
              prefix="₹"
            />
          </Card>
        </Col>
      </Row>

      <Card size="small" title={
        <Space>
          <LineChartOutlined /> <span>Per-Day Breakdown</span>
        </Space>
      }>
        {!plantId ? <Empty description="Select a plant" /> : (
          <Table
            size="small"
            rowKey="day"
            loading={isLoading}
            dataSource={rows}
            pagination={{ pageSize: 31 }}
            onRow={(r) => ({
              onClick: () => setDrillDate(r.day),
              style: { cursor: 'pointer' },
            })}
            columns={[
              { title: 'Day', dataIndex: 'day', width: 110,
                render: (v: string) => <Text strong>{dayjs(v).format('DD-MMM-YYYY')}</Text> },
              { title: 'Laydowns', dataIndex: 'laydown_count', width: 90, align: 'right' as const },
              { title: 'Bales', dataIndex: 'bales', width: 80, align: 'right' as const },
              // Sprint 0070 — Gross / Tare / Net split. Net (₹/kg, ₹ total)
              // is the truth; Gross + Tare are shown for traceability.
              { title: 'Gross kg', dataIndex: 'gross_kg', width: 100, align: 'right' as const,
                render: (v: number) => <Text style={{ color: '#1677ff', fontVariantNumeric: 'tabular-nums' }}>{fmt(v, 0)}</Text> },
              { title: '− Tare', dataIndex: 'tare_kg', width: 90, align: 'right' as const,
                render: (v: number) =>
                  v > 0 ? <Text style={{ color: '#fa8c16', fontVariantNumeric: 'tabular-nums' }}>{fmt(v, 0)}</Text> : '—' },
              { title: 'Net kg', dataIndex: 'net_kg', width: 110, align: 'right' as const,
                render: (v: number) => <Text strong style={{ color: '#52c41a', fontVariantNumeric: 'tabular-nums' }}>{fmt(v, 0)}</Text> },
              { title: 'Avg ₹/kg net', dataIndex: 'avg_cost_per_kg', width: 110, align: 'right' as const,
                render: (v: number | null) => v != null ? <Text strong>₹{fmt(v, 2)}</Text> : '—' },
              { title: 'Total Value', dataIndex: 'total_value', width: 140, align: 'right' as const,
                render: (v: number) => <Text strong style={{ color: '#1677ff' }}>{inr(v)}</Text> },
            ]}
          />
        )}
      </Card>

      <Drawer
        title={drillDate ? `Laydowns on ${dayjs(drillDate).format('DD-MMM-YYYY')}` : ''}
        open={!!drillDate}
        width={800}
        onClose={() => setDrillDate(null)}
      >
        <Table
          size="small"
          rowKey="id"
          dataSource={drillRows}
          pagination={false}
          columns={[
            { title: 'Laydown', dataIndex: 'laydown_no', width: 130,
              render: (v: string) => <Text code>{v}</Text> },
            { title: 'Line', dataIndex: 'blowroom_line_name', width: 100,
              render: (v?: string) => v || '—' },
            { title: 'Count', dataIndex: 'count_ne', width: 80,
              render: (v: number | null) => v ? `${Math.round(v)}s` : '—' },
            { title: 'Bales', dataIndex: 'bales', width: 70, align: 'right' as const },
            { title: 'kg', dataIndex: 'weight_kg', width: 100, align: 'right' as const,
              render: (v: number) => fmt(v, 0) },
            { title: '₹/kg', dataIndex: 'cost_per_kg', width: 90, align: 'right' as const,
              render: (v: number | null) => v != null ? `₹${fmt(v, 2)}` : '—' },
            { title: 'Value', dataIndex: 'total_value', width: 110, align: 'right' as const,
              render: (v: number) => <Text strong>{inr(v)}</Text> },
            { title: 'Status', dataIndex: 'status', width: 130,
              render: (v: string) => <Tag>{v}</Tag> },
          ]}
        />
      </Drawer>
    </div>
  )
}
